import { parsePaymentPlanExternalId } from '@progear/finance-core';
import { usePaymentPlan } from '@/hooks/usePaymentPlans';
import { formatEur, type CashFlowEntry, type MoneyMode } from '@/lib/cashFlow';
import { PLAN_STATUS_LABELS_ET, type PlanRequestOptions } from '@/lib/paymentPlans';

/**
 * "Maksekokkulepe" — the agreement cross-reference panel on a materialized
 * payment-plan entry (module 1.8). One plan installment splits into principal +
 * interest calendar entries; this block explains the split on the shared drill
 * surface (L14): it names THIS entry's part, lists the sibling part (clickable
 * via the shared onOpenEntry drill) and shows the whole installment's total plus
 * the agreement header (supplier · status · osamakse n/N). EVERY figure is a
 * stored cent value read from the shared plan-detail query — this component
 * computes nothing (L9). Plan installments carry no VAT, so the NET/GROSS toggle
 * cannot change the displayed figures; the entry's own amount still honours it.
 */

const PART_LABELS = {
  principal: 'Põhiosa',
  interest: 'Intress',
} as const;

export function PaymentPlanSummary({
  entry,
  mode,
  onOpenEntry,
  requestOptions,
}: {
  entry: CashFlowEntry;
  mode: MoneyMode;
  /** Open the sibling part's detail — the same shared drill navigation the
   *  related-documents panel uses (L14). Omitted → plain, non-clickable row. */
  onOpenEntry?: (entryId: string) => void;
  requestOptions?: PlanRequestOptions;
}) {
  const key = parsePaymentPlanExternalId(entry.externalId);
  const { detail } = usePaymentPlan(key?.planId ?? null, requestOptions);
  if (key === null) return null;

  const selfCents = mode === 'net' ? entry.money.net : entry.money.gross;
  const installment = detail?.installments.find((row) => row.seq === key.seq) ?? null;
  const siblingPart = key.part === 'principal' ? 'interest' : 'principal';
  const sibling = installment?.[siblingPart] ?? null;

  return (
    <section className="rounded-lg border border-border bg-card/40" data-testid="payment-plan-summary">
      <header className="flex items-center gap-2 border-b border-border/70 px-3 py-2">
        <h3 className="text-[12px] font-semibold uppercase tracking-wide text-foreground">Maksekokkulepe</h3>
        <span className="ml-auto text-[10px] text-muted-foreground/70">
          {detail
            ? `osamakse nr ${key.seq}${detail.installments.length > 0 ? ` / ${detail.installments.length}` : ''}`
            : `osamakse nr ${key.seq}`}
        </span>
      </header>
      {detail && (
        <div className="flex items-center gap-2 border-b border-border/70 px-3 py-2 text-[11px]">
          <span className="min-w-0 flex-1 truncate text-foreground">{detail.plan.supplier}</span>
          <span className="shrink-0 rounded border border-border px-1 text-[9px] uppercase tracking-wide text-muted-foreground/70">
            {PLAN_STATUS_LABELS_ET[detail.plan.status]}
          </span>
        </div>
      )}
      <ul className="flex flex-col gap-1 px-3 py-3">
        <li className="flex items-center gap-2 rounded border border-transparent px-2 py-1 text-[11px]">
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-1.5">
              <span className="truncate text-foreground">{PART_LABELS[key.part]}</span>
              <span className="shrink-0 rounded border border-border px-1 text-[9px] uppercase tracking-wide text-muted-foreground/70">
                see kirje
              </span>
            </span>
          </span>
          <span className="shrink-0 text-right tabular-nums text-foreground/85">{formatEur(selfCents)}</span>
        </li>
        {sibling && (
          <li className="flex items-center gap-2 rounded border border-transparent px-2 py-1 text-[11px] hover:border-border">
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-1.5">
                {onOpenEntry ? (
                  <button
                    type="button"
                    onClick={() => onOpenEntry(sibling.entryId)}
                    className="truncate text-foreground hover:text-[hsl(var(--primary))]"
                  >
                    {PART_LABELS[siblingPart]}
                  </button>
                ) : (
                  <span className="truncate text-foreground">{PART_LABELS[siblingPart]}</span>
                )}
              </span>
            </span>
            <span className="shrink-0 text-right tabular-nums text-foreground/85">{formatEur(sibling.grossCents)}</span>
          </li>
        )}
        {installment && (
          <li className="mt-1 flex items-center gap-2 rounded border-t border-border/70 px-2 pb-1 pt-2 text-[11px]">
            <span className="min-w-0 flex-1 font-semibold text-foreground">Osamakse kokku</span>
            <span className="shrink-0 text-right font-semibold tabular-nums text-foreground">
              {formatEur(installment.totalCents)}
            </span>
          </li>
        )}
      </ul>
    </section>
  );
}
